import { useNavigate } from '@tanstack/react-router';
import { Bell, X } from 'lucide-react';
import { useToastStore } from '@/stores/toast-store';

/**
 * Bandeaux de notification « dans le navigateur » (Paramètres › Préférences ›
 * Notifications). Coin inférieur droit, empilés, fermés seuls après quelques secondes.
 *
 * `aria-live="polite"` : un lecteur d'écran annonce le bandeau sans interrompre ce que
 * l'utilisateur est en train de faire — une notification n'est jamais une urgence ici.
 */
export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);
  const navigate = useNavigate();

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="pointer-events-auto flex items-start gap-3 rounded-2xl bg-white p-4 shadow-raised ring-1 ring-ink-100"
        >
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-100 text-brand-700">
            <Bell aria-hidden className="size-4" />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ink-900">{toast.title}</p>
            {toast.body ? <p className="mt-0.5 line-clamp-3 text-xs text-ink-500">{toast.body}</p> : null}
            {toast.actionUrl ? (
              <button
                type="button"
                onClick={() => {
                  dismiss(toast.id);
                  void navigate({ href: toast.actionUrl as string });
                }}
                className="mt-2 text-xs font-semibold text-brand-700 hover:underline"
              >
                Voir
              </button>
            ) : null}
          </div>

          <button
            type="button"
            aria-label="Fermer la notification"
            onClick={() => dismiss(toast.id)}
            className="grid size-6 shrink-0 place-items-center rounded-md text-ink-300 transition hover:text-ink-700"
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
